<template>
  <div id="billing-form">
    <div
      v-show="isProcessing"
      class="cf-flex cf-flex--column cf-flex--align-center cf-flex--justify-center cf-flex--gap-16 cf-modal-loader-container"
    >
      <div class="cf-loader" />

      <p class="cf-subtitle cf-subtitle--2">
        Processing...
      </p>
    </div>

    <div
      v-show="!isProcessing"
      class="cf-flex cf-flex--column cf-flex--gap-16"
    >
      <div class="cf-flex cf-flex--column cf-flex--gap-16">
        <div class="cf-flex cf-flex--row cf-flex--gap-16">
          <!-- Portal to Modal header -->
          <Teleport
            v-if="!paymentModalTitle"
            defer
            to="#portal-modal-header-target"
          >
            {{ content.PaymentScreen.title }}
          </Teleport>

          <!-- Sign in button -->
          <button
            v-if="userAccountFeatureEnabled && !currentUser.accountId && !hiddenElements.signIn"
            class="button button--secondary button--small button--auto-width normalcase"
            type="button"
            @click="signInClickHandler"
          >
            {{ content.PaymentScreen.metadata.signInCta }}
          </button>
        </div>
      </div>

      <!-- Sign in button reminder-->
      <div
        v-show="showLoginReminder && !hiddenElements.signIn"
        class="cf-flex cf-flex--row cf-flex--align-center"
      >
        <p class="cf-subtitle cf-subtitle--2">
          {{ content.PaymentScreen.metadata.checkoutSignInReminder }}
        </p>
      </div>

      <slot name="top" />

      <div
        v-if="loadingAltPaymentMethods"
        class="cf-section-loader-container"
      >
        <div class="cf-loader" />
      </div>

      <!-- Alt Payments (AP/GP etc..) -->
      <transition
        v-else
        name="collapse-fade"
      >
        <cf-alternative-payment-providers
          v-if="!hiddenElements.altPaymentMethods && (total > 0 && totalRemainingBalance > 0) || currentCapiOrderType === 'TipOnly'"
          ref="selectableMethods"
          :hidden-elements="hiddenElements"
          :payment-gateway="paymentGateway"
          :force-save-cards="forceSaveCards"
          :gift-card-reload-payment-method="giftCardReloadPaymentMethod"
          :selected-payment-method="selectedPayment.method"
          :total="totalRemainingBalance"
          :show-gift-card-button="showGiftCardButton"
          :show-room-charge-button="showRoomChargeButton"
          @method-select="handlePaymentMethodSelect"
          @unsupported-method="methodName => unsupportedPaymentMethods.push(methodName)"
          @error="paymentMethodErrorHandler"
          @before-submit="validatePaymentMethod"
          @submitted="paymentMethodSubmittedHandler"
        />
      </transition>

      <!-- Applied Gift Card Balance Notice -->
      <div
        v-if="totalRemainingBalance === 0 && totalGiftCardBalanceApplied > 0"
        class="cf-flex cf-flex--row cf-flex--align-center animation-fade-in"
      >
        <p class="cf-subtitle cf-subtitle--2">
          Your gift {{ giftCards.appliedCards.length === 1 ? 'card currently covers' : 'cards currently cover' }}
          the full balance of your order. Deselect to use a different payment method.
        </p>
      </div>

      <cf-validated-form
        v-if="displaySubmitForm"
        ref="paymentForm"
        name="cardInformation"
        @valid-submit="submitPaymentHandler"
      >
        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <transition name="collapse-fade">
            <div
              v-if="paymentGateway.methods.creditCard && (totalRemainingBalance > 0 || currentCapiOrderType === 'TipOnly')"
              class="cf-flex cf-flex--column cf-flex--gap-12"
            >
              <!-- Saved Credit Card Section -->
              <div
                v-if="saveCreditCardFeatureEnabled && !noPreviouslySavedCards"
                class="cf-flex cf-flex--column cf-flex--gap-12"
              >
                <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
                  <h3 class="cf-title cf-title--3">
                    {{ content.PaymentScreen.metadata.savedCards }}
                  </h3>

                  <!-- Add New Credit Card Button -->
                  <button
                    v-if="!displayCardInformationForm"
                    class="cf-button cf-button--secondary cf-button--fixed-32"
                    type="button"
                    title="Add New Card"
                    @click="addNewPaymentHandler"
                  >
                    <span class="cf-icon far fa-plus" />
                  </button>
                </div>

                <!-- Saved Credit Cards List -->
                <cf-saved-credit-cards
                  v-model="selectedPayment.cardId"
                  :cvv-input="cvv"
                  @saved-card-focus="revealContactInfo"
                  @cvv-input-change="cvvInputChangeHandler"
                />
              </div>

              <!-- Credit Card Form Section -->
              <transition name="fade-down-only-in">
                <div
                  v-if="displayCardInformationForm"
                  class="cf-flex cf-flex--column cf-flex--gap-16"
                >
                  <cf-title-separator
                    v-if="saveCreditCardFeatureEnabled && !noPreviouslySavedCards"
                    :omit-title="true"
                  />

                  <!-- Credit Card Form -->
                  <cf-credit-card-form
                    ref="cardInformation"
                    :payment-gateway="paymentGateway"
                    :tds-provider="tdsProvider.threeDsProvider"
                    :include-shipping-address-for-all-cards="!!tdsProvider.includeShippingAddressForAllCards"
                    :require-billing-address="requireBillingAddress"
                    :skip-required-validation="selectedPayment.method !== 'CreditCard'"
                    @success="cardInformationSuccessHandler"
                    @interacted="cardInformationInteractionHandler"
                    @error="cardInformationErrorHandler"
                  />

                  <!-- Save Payment Checkbox -->
                  <cf-checkbox-input
                    v-if="saveCreditCardFeatureEnabled"
                    v-model="saveNewPaymentMethod"
                    name="shouldSaveCard"
                    :disabled="forceSaveCards"
                  >
                    Save card for future orders
                  </cf-checkbox-input>

                  <h5
                    v-if="saveCreditCardFeatureEnabled && content.PaymentScreen.metadata.addCreditCardTerms"
                    class="cf-title cf-title--5 cf-text-align-left"
                    v-html="content.PaymentScreen.metadata.addCreditCardTerms"
                  />
                </div>
              </transition>

              <!-- Credit Card Surcharge banner -->
              <div
                v-if="!!creditCardSurcharge"
                class="cf-flex cf-flex--row cf-flex--gap-18"
              >
                <p class="cf-subtitle cf-subtitle--2">
                  A <span class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy"> {{ creditCardSurcharge.surchargeAmount }}% surcharge </span>will be added for credit cards.
                </p>

                <button
                  type="button"
                  class="cursor-pointer auto-spacing-right"
                  @click="$openModal('notice', { title: creditCardSurcharge.surchargeModalTitle , description: creditCardSurcharge.surchargeModalDescription , useSecondaryStyle: true })"
                >
                  <span class="cf-icon far fa-info-circle" />
                </button>
              </div>
            </div>
          </transition>

          <!-- Gift Cards List -->
          <cf-saved-gift-cards
            v-if="!hiddenElements.giftCards"
            :payment-gateway="paymentGateway"
            :can-apply-gift-cards="!hasReachedAppliedCardLimit && totalRemainingBalance > 0"
            :gift-cards="giftCards.cards"
          >
            <template #below-listed-cards>
              <transition-group name="fade">
                <p
                  v-if="totalRemainingBalance > 0 && hasReachedAppliedCardLimit"
                  key="card-limit-message"
                  class="cf-subtitle cf-subtitle--2"
                >
                  You've applied the limit of {{ merchant.features['gift-card-limit-on-order'] }}
                  gift card{{ Number(merchant.features['gift-card-limit-on-order']) > 1 ? 's' : '' }} to this order
                </p>

                <p
                  v-if="totalRemainingBalance > 0 && totalGiftCardBalanceApplied > 0"
                  key="select-additional-payment-message"
                  class="cf-subtitle cf-subtitle--2"
                >
                  Please select an additional payment method above to cover the remaining balance: {{ dollars(totalRemainingBalance) }}
                </p>
              </transition-group>
            </template>
          </cf-saved-gift-cards>

          <!-- Contact Information Section -->
          <div
            v-if="isContactInfoVisible"
            class="cf-flex cf-flex--column cf-flex--gap-12"
          >
            <template v-if="currentUser.accountId && hiddenElements.contactInfo === 'read-only'">
              <h3 class="cf-title cf-title--3 cf-text-align-left">
                Contact Info
              </h3>

              <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10">
                <span class="cf-icon icon--smedium fal fa-envelope dark-gray" />

                <p class="cf-subtitle cf-subtitle--2 cf-text-align-left">
                  You’ll recieve a confirmation email at:

                  <br>

                  <span class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
                    {{ currentUser.email }}
                  </span>
                </p>
              </div>
            </template>

            <!-- Contact Information -->
            <cf-contact-information
              v-else
              v-model="contactInfo"
              class="animation-fade-in"
              :email-input-state="emailInputState"
              :phone-is-required="phoneIsRequired"
              :tds-provider="tdsProvider.threeDsProvider"
              :request-billing-email="requestBillingEmail"
              :request-billing-phone="requestBillingPhone"
            />

            <!-- Text Receipt Opt In-->
            <cf-sms-receipt-opt-in v-if="!hiddenElements.smsReceipt" />
          </div>

          <!-- Footer -->
          <div class="cf-modal__footer cf-modal__footer--sticky">
            <div
              ref="footer"
              :class="[
                { 'cf-grid cf-grid--2-grid-column-equal': !hiddenElements.total },
                { 'cf-flex': !hiddenElements.total}
              ]"
            >
              <transition name="fade-up-only-in">
                <div v-if="showTotalBreakdown">
                  <div class="right spacing">
                    <button
                      class="cf-button cf-button--secondary cf-button--sm"
                      type="button"
                      @click="toggleTotalBreakDownHandler"
                    >
                      <span class="cf-icon far fa-times" />
                    </button>
                  </div>

                  <div class="cf-key-value cf-key-value--small">
                    <dl class="cf-key-value__row">
                      <dt class="cf-key-value__key">
                        Your Total
                      </dt>
                      <dd class="cf-key-value__value">
                        {{ dollars(total) }}
                      </dd>
                    </dl>

                    <dl class="cf-key-value__row">
                      <dt class="cf-key-value__key">
                        Applied Gift Card Balance
                      </dt>
                      <dd class="cf-key-value__value">
                        -{{ dollars(totalGiftCardBalanceApplied) }}
                      </dd>
                    </dl>

                    <dl class="cf-key-value__row">
                      <dt class="cf-key-value__key">
                        Remaining Balance Due
                      </dt>

                      <dd class="cf-key-value__value">
                        {{ dollars(totalRemainingBalance) }}
                      </dd>
                    </dl>
                  </div>

                  <hr>
                </div>
              </transition>

              <div
                v-if="!hiddenElements.total"
                class="cf-flex cf-flex--column cf-flex--align-start"
              >
                <p class="cf-subtitle cf-subtitle--2">
                  <template v-if="totalRemainingBalance !== total">
                    Balance Due
                  </template>

                  <template v-else>
                    {{ paymentTotalTitle }}
                  </template>
                </p>

                <div class="cf-flex cf-flex--row cf-flex--align-center">
                  <h2 class="cf-title cf-title--2">
                    {{ dollars(totalRemainingBalance) }}
                  </h2>

                  <button
                    v-if="totalRemainingBalance !== total"
                    v-show="!showTotalBreakdown"
                    class="cf-button cf-button--fixed-40"
                    type="button"
                    @click="toggleTotalBreakDownHandler"
                  >
                    <span class="cf-icon fal fa-info-circle" />
                  </button>
                </div>
              </div>

              <button
                class="cf-button cf-button--primary cf-button--lg cf-button--fill"
                type="submit"
                :disabled="isSubmitPaymentDisabled"
              >
                {{ paymentFormSubmitButtonText }}
              </button>
            </div>
          </div>
        </div>
      </cf-validated-form>
    </div>

    <modal
      v-slot="{ data }"
      name="missingContactInformation"
      modal-style="compact"
      :display-header-title="false"
      :is-required="true"
    >
      <cf-validated-form
        name="missingContactInformation"
        @valid-submit="submitMissingContactInfoHandler(data.options)"
      >
        <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-bottom-16">
          <h2 class="cf-title cf-title--2">
            Almost done!
          </h2>

          <p class="cf-subtitle cf-subtitle--2">
            <template v-if="data.emailIsInvalid && data.phoneIsInvalid">We just need some extra contact info before we can submit your order:</template>
            <template v-else-if="data.phoneIsInvalid">We just need your phone number in case we have to contact you about your order:</template>
            <template v-else-if="data.emailIsInvalid">We just need your email so we can send you a receipt:</template>
          </p>

          <cf-text-input
            v-if="data.emailIsInvalid"
            v-model="email"
            type="email"
            label="Email"
            name="emailMissingCheckout"
            :validations="{
              required: true
            }"
          />

          <cf-phone-number-input
            v-if="data.phoneIsInvalid"
            v-model="phoneNumber"
            label="Phone Number"
            name="phoneMissingCheckout"
            :validations="{
              required: true
            }"
          />

          <button
            class="cf-button cf-button--primary cf-button--lg cf-button--fill"
            type="submit"
          >
            Submit
          </button>
        </div>
      </cf-validated-form>
    </modal>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import dollars from '@/filters/dollars';

  import CfAlternativePaymentProviders from '../cf-alternative-payment-providers/index.vue';
  import CfSavedGiftCards from '../cf-saved-gift-cards/index.vue';
  import CfCreditCardForm from '../cf-credit-card-form/index.vue';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfCheckboxInput from '@/view/components/shared/cf-checkbox-input/index.vue';
  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';

  import CfSavedCreditCards from '@/view/components/globals/cf-saved-credit-cards/index.vue';
  import CfSmsReceiptOptIn from '@/view/components/globals/cf-sms-receipt-opt-in/index.vue';
  import CfContactInformation from '@/view/components/globals/cf-contact-information/index.vue';


  export default {
    name: 'CfPaymentInformation',

    components: {
      CfAlternativePaymentProviders,
      CfSavedGiftCards,
      CfCreditCardForm,
      CfContactInformation,
      CfSavedCreditCards,
      CfSmsReceiptOptIn,
      CfValidatedForm,
      CfCheckboxInput,
      CfPhoneNumberInput,
      CfTextInput,
      CfTitleSeparator
    },

    props: {
      emailInputState: {
        type: String,
        default: 'required',
        validator(value) {
          return ['required', 'optional', 'hidden'].includes(value);
        }
      },

      forceSaveCards: {
        type: Boolean,
        default: false
      },

      hiddenElements: {
        type: Object,
        default: () => ({})
      },

      isPreAuth: {
        type: Boolean,
        default: false
      },

      loadingAltPaymentMethods: {
        type: Boolean,
        default: false
      },

      payCtaText: {
        type: String,
        default: 'Pay'
      },

      paymentGateway: {
        type: Object,
        required: true
      },

      paymentModalTitle: {
        type: String,
        default: null
      },

      paymentTotalTitle: {
        type: String,
        default: 'Your Total'
      },

      phoneIsRequired: {
        type: Boolean,
        default: false
      },

      giftCardReloadPaymentMethod: {
        type: Object,
        default: null
      },

      roomNumber: {
        type: String,
        default: null
      },

      senderInfo: {
        type: Object,
        default() {
          return {
            email: null,
            fullName: null
          };
        }
      },

      tip: {
        type: Number,
        default: 0
      },

      total: {
        type: Number,
        required: true
      },

      tdsProvider: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        contactInfo: {},
        email: null,
        isProcessing: false,
        phoneNumber: null,
        showContactInfo: false,
        showTotalBreakdown: false,
        saveNewPaymentMethod: true,
        /**
         * selectedPayment.method === 'CreditCard' || 'GooglePay' || 'ApplePay'
        */
        selectedPayment: {
          method: 'CreditCard',
          cardId: null
        },
        cvv: null,
        tdsService: {},
        cardInfo: null,
        unsupportedPaymentMethods: []
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('giftCards', ['giftCards', 'hasReachedAppliedCardLimit']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('offers', ['offers']),
      ...mapGetters('orders', ['order', 'currentCapiOrderType']),
      ...mapGetters('savedCards', ['savedCards']),
      ...mapGetters('session', ['currentUser', 'orderDetails', 'session']),

      requireBillingAddress() {
        const selectedLocationCreditCardMethod = this.selectedLocation.paymentGateway.methods.creditCard;

        return selectedLocationCreditCardMethod ? selectedLocationCreditCardMethod.requireFullBillingAddress
          : this.merchant.defaultPaymentGateway.methods.creditCard.requireFullBillingAddress;
      },

      requestBillingEmail() {
        const selectedLocationCreditCardMethod = this.selectedLocation.paymentGateway.methods.creditCard;

        return selectedLocationCreditCardMethod ? !!selectedLocationCreditCardMethod.requestBillingEmail
          : !!this.merchant.defaultPaymentGateway.methods.creditCard.requestBillingEmail;
      },

      requestBillingPhone() {
        const selectedLocationCreditCardMethod = this.selectedLocation.paymentGateway.methods.creditCard;

        return selectedLocationCreditCardMethod ? !!selectedLocationCreditCardMethod.requestBillingPhone
          : !!this.merchant.defaultPaymentGateway.methods.creditCard.requestBillingPhone;
      },

      noPreviouslySavedCards() {
        return this.savedCards.hasLoaded && !this.savedCards.cards.length;
      },

      userAccountFeatureEnabled() {
        return this.merchant.options.signUpEnabled;
      },

      saveCreditCardFeatureEnabled() {
        return [
          this.userAccountFeatureEnabled,
          this.session.isSignedIn,
          !this.merchant.features['disable-saved-payment-management'],
          !!this.paymentGateway.methods.savedCreditCard,
          !this.merchant.features['disable-ordering']
        ].every(Boolean);
      },

      displayCardInformationForm() {
        const noSavedCardSelected = !this.selectedPayment.cardId;

        if (this.paymentGateway.methods.creditCard?.active) {
          if (this.selectedPayment.method === 'SavedCard') {
            return false;
          }

          if (this.selectedPayment.method !== 'CreditCard' && noSavedCardSelected && !this.savedCards.cards.length) {
            return true;
          }

          return this.selectedPayment.method === 'CreditCard';
        }

        return false;
      },

      isContactInfoVisible() {
        return this.hiddenElements.contactInfo !== true && (
          (this.totalRemainingBalance === 0 && this.currentCapiOrderType !== 'TipOnly')
          || this.hiddenElements.contactInfo === 'read-only'
          || this.showContactInfo
        );
      },

      appliedGiftCards() {
        return this.totalGiftCardBalanceApplied > 0 ? this.giftCards.appliedCards : [];
      },

      displaySubmitForm() {
        return !!this.paymentGateway.methods.creditCard || !!this.paymentGateway.methods.giftCard || this.total === 0;
      },

      showLoginReminder() {
        return !this.session.isSignedIn && !!this.content.PaymentScreen.metadata.checkoutSignInReminder;
      },

      paymentFormSubmitButtonText() {
        return this.total === 0 && this.currentCapiOrderType !== 'TipOnly'
          ? 'Submit'
          : this.payCtaText;
      },

      isSubmitPaymentDisabled() {
        return this.unsupportedPaymentMethods.includes(this.selectedPayment.method)
          || (this.totalRemainingBalance > 0 && !this.paymentGateway.methods.creditCard);
      },

      totalGiftCardBalanceApplied() {
        if (this.hiddenElements.giftCards) {
          return 0;
        }

        return Math.min(this.total, this.giftCards.totalBalanceApplied);
      },

      totalRemainingBalance() {
        const total = Math.round((this.total - this.totalGiftCardBalanceApplied) * 100) / 100;

        return Math.max(0, total);
      },

      areGiftCardsLoading() {
        return this.$store.state.giftCards.areLoading;
      },

      showGiftCardButton() {
        return this.paymentGateway.methods.giftCard
          && !this.hiddenElements.giftCards
          && !this.areGiftCardsLoading
          && this.giftCards.cards.length === 0
          && !this.isPreAuth;
      },

      showRoomChargeButton() {
        return this.paymentGateway.methods.roomCharge && !this.hiddenElements.roomCharge;
      },

      creditCardSurcharge() {
        if (this.paymentGateway.methods.creditCard?.surcharge) {
          const surchargeAmount = this.paymentGateway.methods.creditCard.surcharge.percentAmount;

          return {
            surchargeAmount,
            surchargeModalTitle: 'Credit Card Surcharge',
            surchargeModalDescription: `We impose a surcharge of ${surchargeAmount}% on the transaction amount on credit cards, which is not greater than our cost of acceptance. <b>We do not surcharge debit cards.<b>`
          };
        }

        return null;
      }
    },

    watch: {
      isProcessing: 'isProcessingChangeHandler',

      'savedCards.cards': 'savedCardsChangeHandler',

      'session.isSignedIn': {
        handler(isSignedIn) {
          if (isSignedIn) this.fetchSavedCards();
        }
      },

      'selectedPayment.cardId': {
        handler(newCardId) {
          if (newCardId) this.handlePaymentMethodSelect('SavedCard', newCardId);
        }
      }
    },

    async created() {
      await this.fetchSavedCards();

      this.triggerGoogleEventTracking();
    },

    methods: {
      dollars,
      signInClickHandler() {
        const modalConfig = { onSuccess: () => this.$closeModal('paymentInformation') };

        this.$openModal('signIn', modalConfig);
      },

      async fetchSavedCards() {
        if (this.saveCreditCardFeatureEnabled) await this.$store.dispatch('savedCards/getCards');
      },

      async initializeTdsService() {
        const tdsServiceConfig = {
          order: this.order,
          tdsProviderDetails: this.tdsProvider,
          merchantUuid: this.tdsProvider.merchantUuid
        };

        this.tdsService = await this.tdsProvider.tdsProviderService(tdsServiceConfig);
      },

      savedCardsChangeHandler() {
        /* only handle pre-selecting a payment method once saved cards have been loaded */
        if (this.savedCards.hasLoaded) {
          const defaultCard = this.savedCards.validCards.find(card => card.isUsersDefaultCard);
          const [firstCard] = this.savedCards.validCards;
          const defaultSelectedCardId = defaultCard?.creditCardId || firstCard?.creditCardId || null;

          if (this.giftCardReloadPaymentMethod) {
            /* when setting up auto-reload on a card for the first time */
            if (!this.giftCardReloadPaymentMethod.method) {
              this.handlePaymentMethodSelect('CreditCard', defaultSelectedCardId);
            }
            /* when auto-reload is currently configured with a saved credit card */
            else if (this.giftCardReloadPaymentMethod.creditCardId && this.giftCardReloadPaymentMethod.method === 'SavedCreditCard') {
              this.handlePaymentMethodSelect('CreditCard', this.giftCardReloadPaymentMethod.creditCardId);
            }
            /* when auto-reload is currently configured with an alt payment method */
            else {
              this.handlePaymentMethodSelect(this.giftCardReloadPaymentMethod.method);
            }
          }
          else {
            const methodType = defaultSelectedCardId ? 'SavedCard' : 'CreditCard';

            this.handlePaymentMethodSelect(methodType, defaultSelectedCardId);
          }

          if (this.selectedPayment.cardId) this.revealContactInfo();
        }
      },

      handlePaymentMethodSelect(paymentMethod, cardId = null) {
        this.selectedPayment = { method: paymentMethod, cardId };

        if (paymentMethod !== 'CreditCard' && this.$refs.paymentForm) this.$refs.paymentForm.resetValidation();
      },

      validatePaymentMethod({ method, onValidated }) {
        /** This allows to run validations upon clicking GP/AP if needed in the future */
        this.handlePaymentMethodSelect(method);

        onValidated(method);
      },

      paymentMethodErrorHandler(error) {
        this.isProcessing = false;

        if (error.statusCode !== 'CANCELED') {
          errorHandlers.generic(error, {
            fallbackMessage: 'Unable to process payment.'
          });
        }
      },

      cardInformationSuccessHandler(paymentInformation) {
        const contactInfo = this.senderInfo.fullName
          ? this.senderInfo
          : this.contactInfo;

        this.paymentMethodSubmittedHandler({
          ...paymentInformation,
          contactInfo
        });
      },

      cardInformationInteractionHandler() {
        this.handlePaymentMethodSelect('CreditCard');

        this.revealContactInfo();
      },

      revealContactInfo() {
        this.showContactInfo = true;
      },

      cardInformationErrorHandler(error) {
        this.isProcessing = false;

        errorHandlers.silent(error);
      },

      onCheckoutSuccess() {
        this.$closeModal('paymentInformation');
      },

      onCheckoutError() {
        this.isProcessing = false;

        this.$closeModal('paymentInformation');
      },

      addNewPaymentHandler() {
        this.saveNewPaymentMethod = true;

        this.cardInformationInteractionHandler();
      },

      cvvInputChangeHandler(cvvValue) {
        this.cvv = cvvValue;
      },

      isProcessingChangeHandler(isProcessing) {
        if (isProcessing) {
          this.$lockModal('paymentInformation');
        }

        else {
          this.$unlockModal('paymentInformation');
        }
      },

      async toggleTotalBreakDownHandler() {
        this.showTotalBreakdown = !this.showTotalBreakdown;

        if (this.showTotalBreakdown) {
          await this.$nextTick();

          this.$refs.footer.scrollIntoView({
            block: 'end'
          });
        }
      },

      normalizePaymentInfo(options) {
        const type = options.paymentMethod.body?.paymentType || options.paymentMethod.type;
        let { email, fullName, phoneNumber } = options.contactInfo;

        const shouldInheritAccountInfo = !!this.currentUser.accountId && /apple|google/gi.test(type);
        if (shouldInheritAccountInfo) {
          email = this.currentUser.email || email;
          fullName = this.currentUser.fullName || fullName;
          phoneNumber = this.currentUser.primarySmsNumber || phoneNumber;
        }

        return {
          ...options,
          contactInfo: {
            ...options.contactInfo,
            email,
            fullName,
            phoneNumber
          },
          paymentMethod: {
            ...options.paymentMethod,
            giftCards: this.appliedGiftCards
          },
          onCheckoutError: this.onCheckoutError,
          onCheckoutSuccess: this.onCheckoutSuccess
        };
      },

      validatePaymentContactInfo(options) {
        let isValid = true;

        const emailIsInvalid = (this.emailInputState === 'required' || this.requestBillingEmail)
          && !options.contactInfo.email && !this.hiddenElements.contactInfo;

        const phoneIsInvalid = (this.phoneIsRequired || this.session.smsReceiptOptIn || this.requestBillingPhone)
          && !options.contactInfo.phoneNumber && !this.hiddenElements.contactInfo;

        if (emailIsInvalid || phoneIsInvalid) {
          isValid = false;

          this.$openModal('missingContactInformation', {
            emailIsInvalid,
            options,
            phoneIsInvalid
          });
        }

        return isValid;
      },

      async saveContactInfoToAccount(info) {
        try {
          const { phoneNumber } = info.contactInfo;

          if (!this.currentUser.accountId || !phoneNumber || this.currentUser.primarySmsNumber) {
            return;
          }

          await this.$store.dispatch('session/updateProfile', {
            primarySmsNumber: phoneNumber
          });
        }

        catch (error) {
          errorHandlers.silent(error);
        }
      },

      async saveCardInfo(cardInfo) {
        /* attempt to save credit card */
        const savedCard = await this.$store.dispatch('savedCards/createCard', {
          cardInformation: cardInfo.paymentMethod,
          isSilentNotifiction: true
        });

        /* if save credit card succeeds, return saved-card payload */
        if (savedCard) {
          return {
            ...cardInfo,
            paymentMethod: {
              type: 'saved-card',
              savedCardId: savedCard.creditCardId,
              ...cardInfo.paymentMethod.cardDetails?.cvv && { cvv: cardInfo.paymentMethod.cardDetails.cvv },
              giftCards: cardInfo.paymentMethod.giftCards
            }
          };
        }
        /* else return original cardInfo payload */
        return cardInfo;
      },

      async handlePaymentTypes() {
        const isPayingWithNewCard = ['card', 'third-party-form'].includes(this.cardInfo.paymentMethod.type);
        const shouldSaveCard = isPayingWithNewCard && this.saveCreditCardFeatureEnabled && this.saveNewPaymentMethod;

        const cardInfo = shouldSaveCard
          ? await this.saveCardInfo(this.cardInfo)
          : this.cardInfo;

        this.$emit('checkout', cardInfo);
      },

      handleTdsResponse(response = null) {
        if (response) this.$store.commit('orders/SET_TDS_DATA', response);

        this.handlePaymentTypes();
      },

      handleTdsError() {
        this.$closeModal('paymentInformation');
        this.paymentMethodErrorHandler({});
      },

      async submitValidPayment() {
        const orderMeetsThreeDsThreshold = this.total >= this.tdsProvider.threeDsThreshold;
        /**
         * A merchant can opt a specific payment request out of 3DS when sending it
         * (e.g. they take the card over the phone). The opt-out arrives as a
         * `SkipThreeDsAuth` token in the order's `features` string; honor it as
         * threeDsRequired = false so the guest is never prompted for a challenge.
         */
        const skipThreeDsAuth = this.order.features?.includes('SkipThreeDsAuth');
        const runThreeDs = this.tdsProvider.threeDsRequired && !skipThreeDsAuth && orderMeetsThreeDsThreshold && this.cardInfo.paymentMethod.type === 'card';

        if (runThreeDs && this.tdsProvider.threeDsProvider === 'Clover') {
          /**
           * Clover runs 3DS as a post-payment action loop (see services/pay.js) not a pre-payment token.
           */
          await this.initializeTdsService();
          this.$store.commit('orders/SET_CLOVER_BROWSER_INFO', this.tdsService.getBrowserInfo());
          this.handlePaymentTypes();
        }
        else if (runThreeDs) {
          /**
           * The Paay SDK constructor scans the form DOM for `data-three-ds`
           * inputs and caches the input list — it does not re-scan later.
           * Construct it here, just before verify(), so the SDK sees the
           * current form state (including the AMEX-only shipping inputs,
           * which only render after the user has typed an AMEX card).
           */
          await this.initializeTdsService();
          this.tdsService.submitTdsTransaction(this.handleTdsResponse, this.handleTdsError, this.cardInfo);
        }
        else {
          this.handlePaymentTypes();
        }
      },

      paymentMethodSubmittedHandler(paymentInformation) {
        const cardInfo = this.normalizePaymentInfo(paymentInformation);

        const isValidContactInformation = this.validatePaymentContactInfo(cardInfo);

        if (isValidContactInformation) {
          this.isProcessing = true;

          this.cardInfo = cardInfo;

          this.saveContactInfoToAccount(cardInfo);

          this.submitValidPayment();
        }
      },

      submitPaymentHandler() {
        const contactInfo = this.senderInfo.email
          ? this.senderInfo
          : this.contactInfo;

        const paymentDetails = {
          contactInfo,
          paymentMethod: {
            type: null
          }
        };

        if (this.total === 0 && this.currentCapiOrderType !== 'TipOnly') {
          /**
          * This occurs when the total price of items in the cart is $0
          * or when a discount/reward was applied (and no tip) that results in the user spending $0
          */

          if (this.order.discount) {
            paymentDetails.paymentMethod.type = 'offer';
          }
          else if (this.selectedLocation.allowNoPaymentInfo) {
            paymentDetails.paymentMethod.type = 'none';
          }

          if (paymentDetails.paymentMethod.type) this.paymentMethodSubmittedHandler(paymentDetails);
        }

        else if (this.totalGiftCardBalanceApplied > 0 && this.totalRemainingBalance === 0) {
          paymentDetails.paymentMethod.type = 'gift-card';

          this.paymentMethodSubmittedHandler(paymentDetails);
        }

        else if (this.selectedPayment.cardId) {
          Object.assign(
            paymentDetails,
            {
              paymentMethod: {
                type: 'saved-card',
                savedCardId: this.selectedPayment.cardId,
                ...this.cvv && { cvv: this.cvv }
              }
            }
          );

          this.paymentMethodSubmittedHandler(paymentDetails);
        }

        else if (this.selectedPayment.method === 'GooglePay') {
          this.$refs.selectableMethods.$refs.alternativeMethods.submitMethod(this.selectedPayment.method);
        }

        else if (this.selectedPayment.method === 'ApplePay') {
          this.$refs.selectableMethods.$refs.alternativeMethods.submitMethod(this.selectedPayment.method);
        }

        else {
          this.isProcessing = true;
          this.$refs.cardInformation.submit();
        }
      },

      async submitMissingContactInfoHandler(dataOptions) {
        const options = JSON.parse(JSON.stringify(dataOptions));

        options.contactInfo.email = options.contactInfo.email || this.email;
        options.contactInfo.phoneNumber = options.contactInfo.phoneNumber || this.phoneNumber;

        if (this.currentUser.accountId && this.phoneNumber) {
          try {
            await this.$store.dispatch('session/updateProfile', {
              primarySmsNumber: this.phoneNumber
            });
          }

          catch (error) {
            errorHandlers.silent(error);
          }
        }

        this.$closeModal('missingContactInformation');

        this.paymentMethodSubmittedHandler(options);
      },

      triggerGoogleEventTracking() {
        const { orderItems, tax, orderItemTotal, offers } = this.order;

        const [firstOffer] = offers;

        const eventData = {
          itemInfo: orderItems,
          paymentInfo: {
            total: orderItemTotal,
            tax,
            tip: this.tip,
            ...firstOffer && { promoCode: firstOffer.cardfreeOfferPublicId },
            ...this.order.discount && { discount: this.order.discount }

          }
        };

        this.$googleEventTracking.postEvent('begin_checkout', eventData);
      }
    }
  };
</script>
